import { AlertCircle, Loader2 } from "lucide-react";
import { useMemo, useState } from "react";

import { Card, CardContent } from "@/components/ui/card";
import {
  MetricAlertsList,
  MetricKpiCard,
  MetricRangeSelector,
  resolveMetricRange,
  type MetricRangeValue,
} from "@/features/dashboard/shell";

import {
  useMeddreamCurrentHour,
  useMeddreamHourlyMetrics,
} from "../hooks/useMeddreamMetrics";
import { MEDDREAM_LABELS } from "../labels";
import { MeddreamMetricsPermissionError } from "../services/meddream-metrics";
import { aggregateMeddreamKpis } from "../utils/aggregate";
import { buildMeddreamAlerts } from "../utils/alerts";

import { CurrentHourHeader } from "./CurrentHourHeader";
import { HourlyMetricsTable } from "./HourlyMetricsTable";
import { LoadHeroChart } from "./LoadHeroChart";
import { SessionsActivityChart } from "./SessionsActivityChart";

/**
 * Sección MedDream del hub de métricas (solo lectura).
 */
export function MeddreamSection(): React.JSX.Element {
  const [range, setRange] = useState<MetricRangeValue>(() => resolveMetricRange("today"));

  const {
    data: metrics = [],
    isLoading,
    isFetching,
    error,
  } = useMeddreamHourlyMetrics(range);

  const { data: current, isLoading: currentLoading } = useMeddreamCurrentHour();

  const startMs = range.startMs ?? 0;
  const endMs = range.endMs ?? Date.now();

  const kpis = useMemo(
    () => aggregateMeddreamKpis(metrics, startMs, endMs),
    [metrics, startMs, endMs]
  );

  const alerts = useMemo(
    () => buildMeddreamAlerts(metrics, startMs, endMs),
    [metrics, startMs, endMs]
  );

  const isPermission =
    error instanceof MeddreamMetricsPermissionError ||
    (error instanceof Error && /permiso|permission/i.test(error.message));

  return (
    <section className="space-y-6">
      <div className="flex flex-col gap-1 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
            MedDream
          </h2>
          <p className="text-sm text-muted-foreground">
            Métricas horarias del visor · solo lectura
          </p>
        </div>
        {(isLoading || isFetching) && (
          <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            Actualizando…
          </span>
        )}
      </div>

      <CurrentHourHeader current={current} isLoading={currentLoading} />

      <Card>
        <CardContent className="pt-6">
          <MetricRangeSelector value={range} onChange={setRange} />
        </CardContent>
      </Card>

      {error ? (
        <Card>
          <CardContent className="flex items-start gap-3 py-6 text-sm">
            <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-red-500" />
            <div>
              <p className="font-medium text-red-700 dark:text-red-400">
                {isPermission
                  ? "Sin permiso para leer métricas en Firestore"
                  : "Error al cargar métricas"}
              </p>
              <p className="mt-1 text-muted-foreground">
                {isPermission
                  ? "Verifica las reglas de seguridad de la colección meddream_hourly_metrics."
                  : error.message}
              </p>
            </div>
          </CardContent>
        </Card>
      ) : isLoading ? (
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          {[0, 1, 2, 3].map((i) => (
            <div
              key={i}
              className="h-28 animate-pulse rounded-xl bg-gray-100 dark:bg-gray-800"
            />
          ))}
        </div>
      ) : metrics.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <p className="font-medium text-gray-900 dark:text-gray-100">
              Sin métricas en este rango
            </p>
            <p className="mt-1 text-sm text-muted-foreground">
              El backfill puede estar pendiente o el cron aún no ha escrito
              documentos en meddream_hourly_metrics.
            </p>
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            <MetricKpiCard
              label={MEDDREAM_LABELS.peakConnections.label}
              value={kpis.peakConnections}
              hint={MEDDREAM_LABELS.peakConnections.description}
              sparkline={kpis.sparkPeak}
            />
            <MetricKpiCard
              label={MEDDREAM_LABELS.avgConnections.label}
              value={kpis.avgConnections}
              hint={`${MEDDREAM_LABELS.avgConnections.description} (ponderado del período)`}
              sparkline={kpis.sparkAvg}
            />
            <MetricKpiCard
              label={MEDDREAM_LABELS.sessionActivity.label}
              value={kpis.sessionHourActivity}
              hint={MEDDREAM_LABELS.sessionActivity.description}
              sparkline={kpis.sparkSessions}
            />
            <MetricKpiCard
              label={MEDDREAM_LABELS.restarts.label}
              value={kpis.restartsTotal}
              hint={`${MEDDREAM_LABELS.coverage.label}: ${Math.round(kpis.coverageRatio * 100)}% · ${kpis.hoursWithData}/${kpis.expectedHours} h`}
              sparkline={kpis.sparkRestarts}
              accentClassName={
                kpis.restartsTotal > 0 ? "text-red-600 dark:text-red-400" : undefined
              }
            />
          </div>

          <LoadHeroChart metrics={metrics} />
          <SessionsActivityChart metrics={metrics} />
          <MetricAlertsList alerts={alerts} />
          <HourlyMetricsTable metrics={metrics} isLoading={isFetching && !isLoading} />
        </>
      )}
    </section>
  );
}
